import Toggle from 'react-toggle'
import React, {useEffect, useState} from "react";
import {Button, Col, Form, Row} from "react-bootstrap";
import {FormLabelRequiredAsterisk} from "Components/Form/FormLabelRequiredAsterisk";
import {FileUpload} from "Components/FileUpload/FileUpload";
import {
    ACTIVITY_TYPE_LABEL_BY_IDS, ACTIVITY_TYPE_WEEKLY_ID,
    ACTIVITY_TYPE_YEARLY_ID,
    ActivityTypeSelect
} from "Components/Activities/ActivityTypeSelect";
import {AdminYearDayForm} from "Views/Activities/AdminYearDayForm";
import {useQuery} from "react-query";
import {CenterServiceResponseItem, getMutipleCenterServices} from "Api/Query/CenterQuery";
import {Notificator} from "Services/Notificator/Notificator";
import {useDispatch} from "react-redux";
import {appLoading} from "Store/loadingSlice";
import {Time} from "Components/Clock/Time";
import {MoneyAmount} from "Components/Money/MoneyAmount";
import {ROUTE_PATHS} from "Config/Router/Routes";
import {SelectOptionType} from "Model/Select/SelectOption";
import {RichTextEditor} from "Components/RichTextEditor/RichTextEditor";
import {CenterMultiSelect} from "Components/Center/CenterMultiSelect";
import {AddYearSubmitDto} from "../Modalities/AddYearDayModalForm";
import {ActivityDiscountCrud} from "./ActivityDiscountCrud";
import {ActivityAttributesManager, AttributeItem} from "Views/ActivityAttributes/ActivityAttributesManager";
import {getActivityRelAttributes} from "Api/Query/ActivityQuery";

interface Props {
    onSubmit: (data: any, isValid: boolean) => void,
    defaultValues?: { [key: string]: any }
    isEdit?: boolean,
    activityId?: string
}

export const AdminActivityForm: React.FC<Props> = (props) => {

    const defaultFormData = {
        name: "",
        type: "",
        initDate: "",
        endDate: "",
        initPaymentDate: "",
        endPaymentDate: "",
        description: "",
        frontImage: "",
        backgroundImage: "",
        yearDays: [],
        services: [],
        sessions: [],
        centerIds: [],
        centers: [],
        discounts: [],
        attributes: [],
        cancellationLocked: false,
        inscriptionLocked: false,
        canSelectMultipleYearDays: false,
        knowsHowToSwim: false,
    }
    const [formData, setFormData] = useState<{ [key: string]: any }>(defaultFormData)
    const [centerServices, setCenterServices] = useState<CenterServiceResponseItem[]>([])
    const [defaultAttributes, setDefaultAttributes] = useState<AttributeItem[]>([])
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: boolean }>({})
    const [submitted, setSubmitted] = useState<boolean>(false)
    const [cancellationLocked, setCancellationLocked] = useState<boolean>(false)
    const [inscriptionLocked, setInscriptionLocked] = useState<boolean>(false)
    const [knowsHowToSwim, setKnowsHowToSwim] = useState<boolean>(false)
    const [canSelectMultipleYearDays, setCanSelectMultipleYearDays] = useState<boolean>(false)

    const centerServicesQuery = useQuery({
        enabled: false,
        queryKey: ['adminCenterServicesQuery', formData.centerIds],
        queryFn: () => getMutipleCenterServices(formData.centerIds), // TODO: refactor
        onSuccess: (response: any) => {
            setCenterServices(response.data._result.items)
        }
    })

    const activityRelAttributesQuery = useQuery({
        enabled: !!props.activityId,
        queryKey: ['activityRelAttributes', props.activityId],
        queryFn: () => getActivityRelAttributes(props.activityId!),
        onSuccess: (response: any) => {
            const items = response.data._result.items ?? []
            const mapped: AttributeItem[] = items.map((item: any) => ({
                id: item.activityAttributeId,
                name: item.activityAttributeId,
                value: item.value,
            }))
            setDefaultAttributes(mapped)
            setFormData((prev: any) => ({...prev, attributes: mapped}))
        }
    })

    useEffect(() => {
        if (formData.centerIds.length) {
            centerServicesQuery.refetch()
        } else {
            setCenterServices([])
        }
    }, [formData.centerIds])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(appLoading({value: centerServicesQuery.isLoading || activityRelAttributesQuery.isLoading}))
    }, [centerServicesQuery.isLoading, activityRelAttributesQuery.isLoading])

    useEffect(() => {
        if (!submitted) {
            return
        }

        validate(false)
    }, [formData])

    const submit = () => {
        if (!submitted) {
            setSubmitted(true)
        }

        const isValidForm = validate()
        props.onSubmit(formData, isValidForm)
    }

    useEffect(() => {
        let newFormData: any = props.defaultValues ?? defaultFormData

        // centers
        if (props.defaultValues?.centers) {
            const centerOptions: SelectOptionType[] = []
            props.defaultValues?.centers.map((center: any) => {
                centerOptions.push({
                    label: center.name,
                    value: center.id
                })
            })
            newFormData = {...newFormData, centers: centerOptions}
            newFormData = {...newFormData, centerIds: props.defaultValues?.centerIds}
        }

        if (props.defaultValues?.cancellationLocked !== null) {
            setCancellationLocked(props.defaultValues?.cancellationLocked)
        }

        if (props.defaultValues?.inscriptionLocked !== null) {
            setInscriptionLocked(props.defaultValues?.inscriptionLocked)
        }

        if (props.defaultValues?.knowsHowToSwim !== null) {
            setKnowsHowToSwim(props.defaultValues?.knowsHowToSwim)
        }

        if (props.defaultValues?.canSelectMultipleYearDays !== null) {
            setCanSelectMultipleYearDays(props.defaultValues?.canSelectMultipleYearDays)
        }

        setFormData(newFormData)

    }, [props.defaultValues])

    const validate = (showAlert = true): boolean => {
        const errors: { [key: string]: boolean } = {}

        const requiredFields: string[] = [
            'name', 'type', 'centerIds', 'initDate', 'endDate', 'initPaymentDate', 'endPaymentDate', 'frontImage'
        ]

        requiredFields.map(field => {
            if (!formData[field]) {
                errors[field] = true
            }
        })

        setValidationErrors(errors)

        if (Object.keys(errors).length === 0) {
            return true
        }

        if (showAlert) {
            Notificator.error('Los campos en rojo son obligatorios.', 'Error')
        }

        return false
    }

    useEffect(() => {
        if (cancellationLocked !== formData.cancellationLocked) {
            setFormData({...formData, cancellationLocked: cancellationLocked})
        }
    }, [cancellationLocked]);

    useEffect(() => {
        if (inscriptionLocked !== formData.inscriptionLocked) {
            setFormData({...formData, inscriptionLocked: inscriptionLocked})
        }
    }, [inscriptionLocked]);

    useEffect(() => {
        if (knowsHowToSwim !== formData.knowsHowToSwim) {
            setFormData({...formData, knowsHowToSwim: knowsHowToSwim})
        }
    }, [knowsHowToSwim]);

    return (
        <>

            {/*{JSON.stringify(formData)}*/}

            <Col id={"mainRequestList"}>

                <Row>
                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"name"}>
                            <Form.Label>
                                Nombre
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    name: e.target.value
                                })}
                                required
                                value={formData.name}
                                isInvalid={!!validationErrors.name}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"type"}>
                            <Form.Label>
                                Tipo
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            {props.isEdit && <h5>{ACTIVITY_TYPE_LABEL_BY_IDS[formData.type]}</h5>}

                            {!props.isEdit &&
                                <ActivityTypeSelect
                                    onChange={(option: any) => setFormData({...formData, type: option?.value ?? ""})}
                                    value={formData.type}
                                    classes={!!validationErrors.type ? 'is-invalid' : ''}
                                />
                            }
                        </Form.Group>
                    </Col>

                    <Col xs={12} className={"mb-4"}>
                        <Form.Label>
                            Centro/s
                            &nbsp;<FormLabelRequiredAsterisk/>
                        </Form.Label>
                        <CenterMultiSelect
                            onChange={(options: any) => {
                                let optionValues: string[] = []
                                options.map(opt => optionValues.push(opt.value))

                                let newFormData = formData
                                newFormData = {...newFormData, centerIds: optionValues ?? []}
                                newFormData = {...newFormData, centers: options}
                                setFormData(newFormData)
                            }}
                            values={formData.centers}
                            classes={!!validationErrors.centerIds ? 'is-invalid' : ''}
                        />
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"startDate"}>
                            <Form.Label>
                                Fecha de inicio
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    initDate: e.target.value
                                })}
                                required
                                value={formData.initDate}
                                isInvalid={!!validationErrors.initDate}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"endDate"}>
                            <Form.Label>
                                Fecha fin
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    endDate: e.target.value
                                })}
                                required
                                value={formData.endDate}
                                isInvalid={!!validationErrors.endDate}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"startDate"}>
                            <Form.Label>
                                Fecha de pago inicial
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    initPaymentDate: e.target.value
                                })}
                                required
                                value={formData.initPaymentDate}
                                isInvalid={!!validationErrors.initPaymentDate}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} sm={6} md={6} lg={6} className={"mb-4"}>
                        <Form.Group controlId={"endDate"}>
                            <Form.Label>
                                Fecha de pago final
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    endPaymentDate: e.target.value
                                })}
                                required
                                value={formData.endPaymentDate}
                                isInvalid={!!validationErrors.endPaymentDate}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row className={"my-4"}>
                    <Col>
                        <Form.Group
                            controlId={"cancellationLocked"}
                            className={"mb-4"}
                        >
                            <label
                                htmlFor={"cancellationLocked"}
                                className={"d-block mb-2 fw-bold pointer"}
                            >
                                Bloquear cancelaciones
                            </label>
                            <Toggle
                                id={"cancellationLocked"}
                                checked={cancellationLocked}
                                onChange={() => setCancellationLocked(!cancellationLocked)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group
                            controlId={"inscriptionLocked"}
                            className={"mb-4"}
                        >
                            <label
                                htmlFor={"inscriptionLocked"}
                                className={"d-block mb-2 fw-bold pointer"}
                            >
                                Bloquear inscripciones (solo previsualización)
                            </label>
                            <Toggle
                                id={"inscriptionLocked"}
                                checked={inscriptionLocked}
                                onChange={() => setInscriptionLocked(!inscriptionLocked)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group
                            controlId={"canSelectMultipleYearDays"}
                            className={"mb-4"}
                        >
                            <label
                                htmlFor={"canSelectMultipleYearDays"}
                                className={"d-block mb-2 fw-bold pointer"}
                            >
                                Puede seleccionar múltiples días
                            </label>
                            <Toggle
                                id={"canSelectMultipleYearDays"}
                                checked={canSelectMultipleYearDays}
                                onChange={() => {
                                    const value = !canSelectMultipleYearDays
                                    setCanSelectMultipleYearDays(value)
                                    setFormData({...formData, canSelectMultipleYearDays: value})
                                }}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group
                            controlId={"knowsHowToSwim"}
                            className={"mb-4"}
                        >
                            <label
                                htmlFor={"knowsHowToSwim"}
                                className={"d-block mb-2 fw-bold pointer"}
                            >
                                ¿Sabe nadar?
                            </label>
                            <Toggle
                                id={"knowsHowToSwim"}
                                checked={knowsHowToSwim}
                                onChange={() => setKnowsHowToSwim(!knowsHowToSwim)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group controlId={"description"}>
                            <Form.Label>
                                Descripción
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>

                            <RichTextEditor
                                onChange={(htmlContent) => {
                                    setFormData({...formData, description: htmlContent})
                                }}
                                defaultContent={formData.description}
                            />
                        </Form.Group>

                    </Col>
                </Row>
                <Row className={"mt-4"}>
                    <Col xs={12} sm={6} md={6} lg={6}>
                        <Form.Group controlId={"frontImage"}>
                            <Form.Label>
                                Imagen de portada
                                &nbsp;<FormLabelRequiredAsterisk/>
                            </Form.Label>
                            <div className={!!validationErrors.frontImage ? 'is-invalid-div' : ''}>
                                {
                                    props.defaultValues?.frontImageSrc &&
                                    <Row className={"mb-4"}>
                                        <img src={props.defaultValues?.frontImageSrc} height={250}
                                             style={{"maxWidth": '100%'}}/>
                                    </Row>
                                }

                                <FileUpload
                                    onChange={(files) => files ? setFormData({
                                        ...formData,
                                        frontImage: files[0]
                                    }) : null}
                                    maxFiles={1}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm={6} md={6} lg={6}>
                        <Form.Group controlId={"backgroundImage"}>
                            <Form.Label>
                                Imagen de fondo
                            </Form.Label>
                            {
                                props.defaultValues?.backgroundImageSrc &&
                                <Row className={"mb-4"}>
                                    <img src={props.defaultValues?.backgroundImageSrc} height={250}
                                         style={{"maxWidth": '100%'}}/>
                                </Row>
                            }
                            <FileUpload
                                onChange={(files) => files ? setFormData({
                                    ...formData,
                                    backgroundImage: files[0]
                                }) : null}
                                maxFiles={1}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {
                    formData.type === ACTIVITY_TYPE_YEARLY_ID &&
                    <>
                        <Row className={"mt-4"}>
                            <Col xs={12} sm={12}>
                                <AdminYearDayForm
                                    onChange={(yearDays: AddYearSubmitDto[]) => setFormData({
                                        ...formData,
                                        yearDays: yearDays
                                    })}
                                    yearDays={formData.yearDays}
                                    services={centerServices}
                                />
                            </Col>
                        </Row>
                    </>
                }

                {
                    formData.type === ACTIVITY_TYPE_WEEKLY_ID && props.isEdit &&
                    <>
                        <br/>
                        <Row className={"mt-4"}>
                            <Col xs={12}>
                                <Form.Group controlId={"sessions"}>
                                    <Row className={"d-flex"}>
                                        <Col sm={12} md={6}>
                                            <h3>Modalidades</h3>
                                        </Col>
                                        <Col sm={12} md={6} style={{"textAlign": "right"}}>
                                            <Button
                                                className={"purple-btn btn-sm float-right"}
                                                href={ROUTE_PATHS.ADMIN_MODALITY_CREATE}
                                            >
                                                Crear nueva modalidad
                                            </Button>
                                        </Col>
                                    </Row>

                                    <table className={"table table-bordered redTable"}>
                                        <thead>
                                        <tr>
                                            <th>Nombre</th>
                                            <th>Días</th>
                                            <th>Horario</th>
                                            <th>Precio</th>
                                            <th></th>
                                        </tr>

                                        {
                                            formData.sessions.length === 0 &&
                                            <tr>
                                                <td colSpan={5}>
                                                    Todavía no has creado ninguna modalidad para esta actividad.
                                                </td>
                                            </tr>
                                        }
                                        {
                                            formData.sessions.map((activitySession: any, key: number) => {
                                                return (
                                                    <tr key={key}>
                                                        <td>{activitySession.name}</td>
                                                        <td>
                                                            {activitySession.days.map((day: any, key: number) => {
                                                                return (
                                                                    <span key={key}>
                                                                        {day.name}
                                                                        {key < activitySession.days.length - 1 && ', '}
                                                                    </span>
                                                                )
                                                            })}
                                                        </td>
                                                        <td>
                                                            <Time value={activitySession.start}/> - <Time
                                                            value={activitySession.end}/>
                                                        </td>
                                                        <td>
                                                            <MoneyAmount amount={activitySession.price}/>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                className={"purple-btn btn-sm"}
                                                                href={ROUTE_PATHS.ADMIN_MODALITY_EDIT.replace(":id", activitySession.id)}
                                                            >
                                                                Modificar
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        }

                                        </thead>
                                    </table>

                                </Form.Group>
                            </Col>
                        </Row>
                        <br/>
                    </>
                }

                <br/>
                <hr/>
                <Row className={"mt-4"}>
                    <Col xs={12}>
                        <h3>Atributos</h3>
                        <ActivityAttributesManager
                            defaultValues={defaultAttributes}
                            onChange={(attributes: AttributeItem[]) => setFormData({...formData, attributes})}
                        />
                    </Col>
                </Row>

                {
                    props.activityId && <>
                        <br/>
                        <Row className={"mt-4"}>
                            <Col xs={12}>
                                <ActivityDiscountCrud
                                    activityId={props.activityId}
                                />
                            </Col>
                        </Row>
                    </>
                }

            </Col>

            <Row>
                <Col className={"mt-4"}>
                    <br/>
                    <Button
                        className={"purple-btn btn-sm"}
                        onClick={() => submit()}
                    >
                        Enviar
                    </Button>
                </Col>
            </Row>
        </>
    )
}
